import React from 'react';

import { cn } from '@change/ds-utils';

import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import {
	useInitialize,
	useIsStaticFacesAskVisible,
	useIsVideoFirstArm,
	UploadStepProvider,
	VideoFirstFlowProvider,
} from '#src/app/pages/actions/shared/hooks';
import { JustSignedBar } from '#src/app/shared/components/supporterVoices';

import { Actions } from '../components/Actions';
import { ActionsPageView } from '../components/ActionsPageView';
import { Header } from '../components/Header';
import { HeroImage } from '../components/HeroImage';
import { SkipLink } from '../components/SkipLink';

export const Layout: React.FC = () => (
	<VideoFirstFlowProvider>
		{/*
		 * Mounted above `CaptureVideo` (which mounts it too deep for this file to read) so
		 * `LayoutBody` can read `uploadStep` for the V3-ask-only compact header
		 * (`useIsStaticFacesAskVisible`, CHANGE-99359).
		 */}
		<UploadStepProvider>
			<LayoutBody />
		</UploadStepProvider>
	</VideoFirstFlowProvider>
);

const LayoutBody: React.FC = () => {
	useInitialize();

	const {
		data: { petition },
	} = usePetitionActionsPageData();
	const [{ shareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const isVideoFirstArm = useIsVideoFirstArm();
	const isStaticFacesAskVisible = useIsStaticFacesAskVisible();
	// R1's cosmetic changes and R2's V3 static-faces ask (CHANGE-99359) both need the compact
	// header - the ask screen's gradient reaches up behind it - though neither drives the other.
	const compactHeader = isVideoFirstArm || isStaticFacesAskVisible;

	return (
		<>
			<ActionsPageView />

			<div className="bg-background-raised relative mx-auto max-w-[640px] md:pt-2">
				{compactHeader ? (
					/*
					 * Replaces the hero photo and its overlaid title card, so the screen opens on the ask.
					 * `relative z-10` so the row paints above the ask screen's gradient backdrop, which is
					 * absolute against this container and therefore spans the row too.
					 */
					<JustSignedBar photoUrl={petition.photo?.petitionLarge.url} ask={petition.ask} className="relative z-10" />
				) : (
					<HeroImage />
				)}

				{/* Content is rendered on top of the petition image and then over the gray background. The
				    bar is in flow rather than overlaid, so it needs none of that clearance. */}
				{/*
				 * Not a positioning context on the video-first arms: the ask screen's gradient is absolute
				 * against the container above, so it can span the post-sign header. Control keeps `relative`,
				 * which is what lifts its content above the absolutely-positioned hero image.
				 */}
				<div
					className={cn(
						'flex flex-col px-4 pb-8',
						// eslint-disable-next-line no-nested-ternary
						compactHeader ? 'pt-2' : shareSupporterVoiceSuccess ? 'relative pt-8' : 'relative pt-10',
					)}
				>
					{!compactHeader && <Header />}
					<Actions />
					<SkipLink />
				</div>
			</div>
		</>
	);
};
